import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Table, Form } from 'react-bootstrap';
import { FaArrowUp, FaArrowDown, FaWallet } from 'react-icons/fa';
import { toast } from 'react-toastify';
import transactionService from '../../services/transactionService';
import initialBalanceService from '../../services/initialBalanceService';
import PaginationComponent from '../../components/PaginationComponent';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { usePagination } from '../../hooks/usePagination';
import { useFilters } from '../../hooks/useFilters';

const Dashboard = () => {
  const [monthlyData, setMonthlyData] = useState({
    income: 0,
    expense: 0,
    balance: 0,
    initialBalance: 0,
    transactions: []
  });
  const [loading, setLoading] = useState(true);
  
  // Filtros usando custom hook
  const getDefaultFilters = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    return {
      startDate: firstDay.toISOString().split('T')[0],
      endDate: lastDay.toISOString().split('T')[0]
    };
  };
  
  const { filters, handleFilterChange: updateFilter } = useFilters(
    getDefaultFilters(),
    'dashboardFilters'
  );


  // Paginação usando custom hook
  const {
    currentPage,
    itemsPerPage,
    totalPages,
    currentItems: currentTransactions,
    totalItems,
    handlePageChange,
    handleItemsPerPageChange,
    resetToFirstPage
  } = usePagination(monthlyData.transactions);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Carregar saldo inicial
      const initialBalance = await initialBalanceService.getCurrentBalance();
      
      // Buscar transações do período
      const transactions = await transactionService.search({
        startDate: filters.startDate,
        endDate: filters.endDate,
        orderBy: 'date',
        orderDirection: 'asc'
      });
      
      console.log('Transações carregadas:', transactions.length);
      
      // Calcular saldos
      let currentBalance = initialBalance?.amount || 0;
      const transactionsWithBalance = transactions.map(transaction => {
        const amount = transaction.nature === 'EXPENSE' ? -Math.abs(transaction.amount) : transaction.amount;
        currentBalance += amount;
        return {
          ...transaction,
          balance: currentBalance
        };
      });

      // Calcular totais do período
      let income = 0;
      let expense = 0;
      
      transactions.forEach(transaction => {
        if (transaction.nature === 'INCOME') {
          income += transaction.amount;
        } else {
          expense += Math.abs(transaction.amount);
        }
      });
      
      setMonthlyData({
        income,
        expense,
        balance: income - expense,
        initialBalance: initialBalance?.amount || 0,
        transactions: transactionsWithBalance
      });
      
      // Resetar para a primeira página quando os filtros mudam
      resetToFirstPage();
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  }, [filters.startDate, filters.endDate, resetToFirstPage]);

  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      loadDashboardData();
    }
  }, [filters, loadDashboardData]);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    updateFilter(name, value);
  }, [updateFilter]);







  return (
    <Container>
      <div className="sheet-container">
        <div className="p-3">
          <Row className="mb-4">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Data Inicial</Form.Label>
                <Form.Control
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  className="excel-form-control"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Data Final</Form.Label>
                <Form.Control
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  className="excel-form-control"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={4}>
              <div className="dashboard-card">
                <div className="card-icon income">
                  <FaArrowUp data-testid="income-icon" />
                </div>
                <div className="card-content">
                  <h6>Receitas do Período</h6>
                  <h4 data-testid="income-value" className="text-success">
                    {formatCurrency(monthlyData.income)}
                  </h4>
                </div>
              </div>
            </Col>
            
            <Col md={4}>
              <div className="dashboard-card">
                <div className="card-icon expense">
                  <FaArrowDown data-testid="expense-icon" />
                </div>
                <div className="card-content">
                  <h6>Despesas do Período</h6>
                  <h4 data-testid="expense-value" className="text-danger">
                    {formatCurrency(monthlyData.expense)}
                  </h4>
                </div>
              </div>
            </Col>
            
            <Col md={4}>
              <div className="dashboard-card">
                <div className="card-icon">
                  <FaWallet data-testid="balance-icon" />
                </div>
                <div className="card-content">
                  <h6>Saldo do Período</h6>
                  <h4 
                    data-testid="balance-value"
                    className={monthlyData.balance >= 0 ? 'text-success' : 'text-danger'}
                  >
                    {formatCurrency(monthlyData.balance)}
                  </h4>
                </div>
              </div>
            </Col>
          </Row>

          <div className="mb-4">
            {loading ? (
              <div className="text-center my-4">
                <span className="spinner-border text-primary" role="status"></span>
                <p className="mt-2">Carregando transações...</p>
              </div>
            ) : (
              <>
                <Table className="dashboard-table" responsive>
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Descrição</th>
                      <th>Categoria</th>
                      <th>Cartão</th>
                      <th className="text-end">Valor</th>
                      <th className="text-end">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="balance-header">
                      <td>{filters.startDate ? formatDate(filters.startDate) : ''}</td>
                      <td colSpan="3">Saldo Anterior</td>
                      <td className="text-end"></td>
                      <td className={`text-end ${monthlyData.initialBalance >= 0 ? 'text-success' : 'text-danger'}`}>
                        {formatCurrency(monthlyData.initialBalance)}
                      </td>
                    </tr>
                    {currentTransactions.length > 0 ? (
                      currentTransactions.map((transaction, index) => (
                        <tr key={transaction.id} className={transaction.nature === 'EXPENSE' ? 'expense' : 'income'}>
                          <td>{formatDate(transaction.date)}</td>
                          <td>{transaction.description}</td>
                          <td>{transaction.category?.name || '-'}</td>
                          <td>{transaction.creditCard?.name || '-'}</td>
                          <td className={`text-end ${transaction.nature === 'EXPENSE' ? 'text-danger' : 'text-success'}`}>
                            {formatCurrency(transaction.nature === 'EXPENSE' ? -transaction.amount : transaction.amount)}
                          </td>
                          <td className={`text-end ${transaction.balance >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatCurrency(transaction.balance)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center">Nenhuma transação encontrada no período selecionado</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                
                <PaginationComponent
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={totalItems}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  itemName="transações"
                />
              </>
            )}
          </div>
        </div>
      </div>


    </Container>
  );
};

export default Dashboard; 